import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../../../router/all_routes";
import { useUsers } from "../hooks/useUsers";
import { showToast } from "../../../../../core/utils/alert";
import CommonSelect from "../../../../../core/common/commonSelect";
import {
  useCheckUsernameQuery,
  useGetCenterCredentialsQuery,
} from "../api/usersApi";
import { useCenter } from "../../hooks/useCenter";

const AddUser = () => {
  const navigate = useNavigate();
  const { id: centerId, userId } = useParams<{ id: string; userId: string }>();
  const { saveUserCredentials, isSaving, updateUser, isUpdating } = useUsers();
  const routes = all_routes;
  const isEditMode = !!userId;

  const { useGetCenterById } = useCenter();
  const { data: centerDetails } = useGetCenterById(centerId || "0");
  const centerName = centerDetails?.data?.center?.name || "";

  const { data: userDetails, isLoading: isLoadingUser } =
    useGetCenterCredentialsQuery(parseInt(userId || "0"), {
      skip: !isEditMode,
    });

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    role: "centeradmin",
    status: "Active",
  });

  useEffect(() => {
    if (isEditMode && userDetails?.data?.user) {
      const user = userDetails.data.user;

      let userStatus = user.status || "active";

      // If status is lowercase in API, convert to match our dropdown format
      if (typeof userStatus === "string") {
        if (userStatus.toLowerCase() === "active") {
          userStatus = "Active";
        } else if (userStatus.toLowerCase() === "inactive") {
          userStatus = "Inactive";
        }
      }

      setFormData({
        username: user.username || "",
        password: "",
        role: user.role || "centeradmin",
        status: userStatus,
      });
    }
  }, [isEditMode, userDetails]);

  const statusOptions = [
    { value: "Active", label: "Activate" },
    { value: "Inactive", label: "Deactivate" },
  ];

  // Username validation
  const [usernameDebounce, setUsernameDebounce] = useState("");
  const { data: usernameCheck, isFetching: isCheckingUsername } =
    useCheckUsernameQuery(usernameDebounce, {
      skip:
        !usernameDebounce ||
        usernameDebounce.length < 3 ||
        (isEditMode && usernameDebounce === userDetails?.data?.user?.username),
    });

  // username debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.username && formData.username.length >= 3) {
        // Don't check if it's the original username in edit mode
        if (
          !(
            isEditMode &&
            formData.username === userDetails?.data?.user?.username
          )
        ) {
          setUsernameDebounce(formData.username);
        }
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.username, isEditMode, userDetails?.data?.user?.username]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!centerId) return;

      if (isEditMode && userId) {
        // Check if we changed the username and it's available
        if (
          formData.username !== userDetails?.data?.user?.username &&
          usernameCheck?.data?.available === false
        ) {
          showToast({
            title: "Error",
            text: "Username already exists",
            icon: "error",
            timer: 5000,
          });
          return;
        }

        await updateUser(parseInt(userId), {
          username: formData.username,
          status: formData.status,
        });

        showToast({
          title: "Success",
          text: "User updated successfully",
          icon: "success",
          timer: 5000,
        });

        navigate(routes.centerUsersId.replace(":id", centerId));
      } else {
        // Add mode
        // Check if username exists before submitting
        if (usernameCheck?.data?.available === false) {
          showToast({
            title: "Error",
            text: "Username already exists",
            icon: "error",
            timer: 5000,
          });
          return;
        }

        const payload = {
          username: formData.username,
          password: formData.password,
          role: formData.role,
          center_id: parseInt(centerId),
          status: formData.status,
        };

        await saveUserCredentials(payload);

        showToast({
          title: "Success",
          text: "User added successfully",
          icon: "success",
          timer: 5000,
        });

        navigate(routes.centerUsersId.replace(":id", centerId));
      }
    } catch (error) {
      console.error("Error:", error);
      showToast({
        title: "Error",
        text: isEditMode ? "Failed to update user" : "Failed to add user",
        icon: "error",
        timer: 5000,
      });
    }
  };

  if (isEditMode && isLoadingUser) {
    return <div className="page-wrapper">Loading user details...</div>;
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="mb-1">{isEditMode ? "Edit" : "Add"} User</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link
                    to={routes.centerDetailsId.replace(":id", centerId || "")}
                  >
                    Centers
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link
                    to={routes.centerUsersId.replace(":id", centerId || "")}
                  >
                    Center Users
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {isEditMode ? "Edit" : "Add"} User
                </li>
              </ol>
            </nav>
            {/* Display Center Name */}
            {centerName && (
              <div className="alert alert-info mt-2 p-3 rounded-3 shadow-sm border-0">
                <strong className="text-dark">Center:</strong>{" "}
                <span className="text-primary fw-bold">{centerName}</span>
              </div>
            )}
          </div>
        </div>
        {/* /Page Header */}

        <div className="row">
          <div className="col-md-12">
            <form onSubmit={handleSubmit}>
              {/* Basic Information Section */}
              <div className="card mb-3">
                <div className="card-header bg-light">
                  <div className="d-flex align-items-center">
                    <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                      <i className="ti ti-user fs-16" />
                    </span>
                    <h4 className="text-dark">User Information</h4>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Username <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          name="username"
                          className={`form-control ${
                            usernameCheck?.data?.available === false
                              ? "is-invalid"
                              : usernameCheck?.data?.available === true
                              ? "is-valid"
                              : ""
                          }`}
                          placeholder="Enter username"
                          value={formData.username}
                          onChange={handleInputChange}
                          required
                          minLength={3}
                        />
                        {isCheckingUsername && (
                          <small className="text-muted">
                            Checking username...
                          </small>
                        )}
                        {usernameCheck?.data?.available === false && (
                          <div className="invalid-feedback">
                            {usernameCheck.message ||
                              "Username is already taken"}
                          </div>
                        )}
                        {formData.username && formData.username.length < 3 && (
                          <small className="text-muted">
                            Username must be at least 3 characters
                          </small>
                        )}
                      </div>
                    </div>
                    {!isEditMode && (
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Password <span className="text-danger">*</span></label>
                          <input
                            type="password"
                            name="password"
                            className="form-control"
                            placeholder="Enter password"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                    )}
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Role</label>
                        <input
                          type="text"
                          name="role"
                          className="form-control"
                          placeholder="Enter role"
                          value={formData.role}
                          onChange={handleInputChange}
                          disabled
                        />
                        <small className="text-muted">
                          Default role is set to "centeradmin"
                        </small>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Status</label>
                        <input
                          type="text"
                          name="status"
                          className="form-control"
                          placeholder="Status"
                          value={formData.status}
                          onChange={handleInputChange}
                          disabled
                        />
                        <small className="text-muted">
                          Status is set to "Active" for new users
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-end">
                <Link
                  to={routes.centerUsersId.replace(":id", centerId || "")}
                  className="btn btn-light me-3"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    isUpdating ||
                    isSaving ||
                    isCheckingUsername ||
                    (usernameCheck?.data?.available === false &&
                      formData.username !== userDetails?.data?.user?.username)
                  }
                >
                  {isUpdating
                    ? "Updating..."
                    : isSaving
                    ? "Saving..."
                    : isEditMode
                    ? "Update User"
                    : "Add User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
